import { EMAIL_ADDRESS, WhatsAppLogo, LINKEDIN_URL, WHATSAPP_NUMBER } from "@/lib";
import { motion } from "framer-motion";
import { Linkedin, Mail, Phone, ArrowRight, Loader2 } from "lucide-react";
import { useState } from "react";

export default function ContactSection(){
    const [formState, setFormState] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormState({ ...formState, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        if (!formState.name || !formState.email || !formState.message) {
            setStatus('Please fill in all fields before submitting.');
            setIsSubmitting(false);
            return;
        }

        // Simulate sending via WhatsApp
        const messageBody = `*New Inquiry via Website Form*\n\nName: ${formState.name}\nEmail: ${formState.email}\n\nMessage: ${formState.message}`;
        const encodedMessage = encodeURIComponent(messageBody);
        const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
        
        setStatus('Success! Opening WhatsApp chat with your message...');
        setFormState({ name: '', email: '', message: '' });

        setTimeout(() => {
            setStatus('');
            setIsSubmitting(false);
        }, 5000); 
    };
    
    // Framer Motion variants for staggered entry
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
    };

    return (
        <section id="contact" className="py-24 bg-slate-950 relative overflow-hidden">
             {/* Subtle background glow */}
             <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-indigo-700/10 rounded-full blur-[150px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-sm font-semibold uppercase text-sky-400 tracking-wider">Get in Touch</span>
                    <h2 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">
                            Let's Discuss Your Strategy
                        </span>
                    </h2>
                    <p className="mt-4 text-lg text-gray-400">
                        I am available for technical consulting, interim CTO roles, and strategic advisory mandates.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    
                    {/* LEFT COLUMN: Contact Details Card */}
                    <motion.div 
                        className="p-1 rounded-2xl bg-gradient-to-br from-indigo-800/50 to-slate-900/50 shadow-3xl h-full"
                        initial="hidden"
                        whileInView="visible"
                        variants={containerVariants}
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <div className="bg-slate-900/90 p-8 md:p-10 rounded-xl h-full">
                            <h3 className="text-2xl font-bold text-white mb-8 border-b border-slate-800 pb-3">
                                Direct Channels
                            </h3>
                            
                            <div className="space-y-6">
                                {[
                                    { icon: Mail, title: 'Email', value: EMAIL_ADDRESS, href: `mailto:${EMAIL_ADDRESS}` },
                                    { icon: Phone, title: 'Phone', value: '+977 9813762963', href: 'tel:+9779813762963' },
                                    { icon: Linkedin, title: 'LinkedIn', value: 'Connect Professionally', href: LINKEDIN_URL, target: '_blank' }
                                ].map((item, index) => (
                                    <motion.a 
                                        key={item.title} 
                                        href={item.href}
                                        target={item.target || '_self'}
                                        rel={item.target ? "noopener noreferrer" : ""}
                                        className="group flex items-start p-4 -mx-4 rounded-lg transition-all duration-300 hover:bg-slate-800/70 hover:shadow-lg hover:shadow-indigo-500/10"
                                        variants={itemVariants}
                                    >
                                        <item.icon className="w-6 h-6 text-sky-400 mr-4 flex-shrink-0 mt-1 transition-colors group-hover:text-white" />
                                        <div>
                                            <h3 className="text-lg font-semibold text-white group-hover:text-sky-300 transition-colors">{item.title}</h3>
                                            <p className="text-md text-gray-300">{item.value}</p>
                                        </div>
                                        <ArrowRight className="w-5 h-5 ml-auto mt-2 text-slate-600 opacity-0 group-hover:opacity-100 group-hover:text-sky-400 transition-all duration-300" />
                                    </motion.a>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* RIGHT COLUMN: Contact Form */}
                    <motion.form 
                        onSubmit={handleSubmit} 
                        className="bg-slate-900 p-8 rounded-2xl shadow-2xl border border-slate-800/50 relative"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {status && (
                            <motion.div 
                                initial={{ opacity: 0, y: -10 }} 
                                animate={{ opacity: 1, y: 0 }} 
                                className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                                    status.includes('Success') 
                                        ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-600/50' 
                                        : 'bg-red-900/50 text-red-400 border border-red-600/50'
                                }`}
                            >
                                {status}
                            </motion.div>
                        )}
                        <div className="space-y-6">
                            {/* Input Fields */}
                            {['name', 'email'].map((field) => (
                                <div key={field}>
                                    <label htmlFor={field} className="block text-sm font-medium text-gray-300 capitalize mb-1">{field} / Role</label>
                                    <input 
                                        type={field === 'email' ? 'email' : 'text'} 
                                        name={field} 
                                        id={field} 
                                        value={(formState as any)[field]} 
                                        onChange={handleChange} 
                                        required 
                                        className="block w-full bg-slate-800/50 border border-slate-700 rounded-xl p-3 text-white shadow-md focus:ring-sky-500 focus:border-sky-500 focus:shadow-xl focus:shadow-indigo-500/10 transition-all" 
                                        placeholder={field === 'email' ? 'you@company.com' : 'Full Name'}
                                    />
                                </div>
                            ))}
                            
                            {/* Message Area */}
                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">Project Details / Message</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    rows={5} 
                                    value={formState.message} 
                                    onChange={handleChange} 
                                    required 
                                    className="block w-full bg-slate-800/50 border border-slate-700 rounded-xl p-3 text-white shadow-md focus:ring-sky-500 focus:border-sky-500 focus:shadow-xl focus:shadow-indigo-500/10 transition-all resize-none" 
                                    placeholder="Tell me about your goals, timelines, and technical requirements..."
                                ></textarea>
                            </div>
                            
                            {/* Submit Button */}
                            <motion.button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-xl text-base font-semibold text-gray-900 bg-gradient-to-r from-sky-400 to-cyan-300 transition-all disabled:opacity-50"
                                whileHover={{ scale: 1.02, boxShadow: "0 10px 20px rgba(14, 165, 233, 0.4)" }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {isSubmitting ? (
                                    <motion.span 
                                        animate={{ rotate: 360 }} 
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    >
                                        <Loader2 className="w-5 h-5 mr-2 text-gray-900" />
                                    </motion.span>
                                ) : (
                                    <WhatsAppLogo className="w-5 h-5 mr-2 text-gray-900" />
                                )}
                                {isSubmitting ? 'Preparing Chat...' : 'Send Inquiry via WhatsApp'}
                            </motion.button>
                            <p className="text-center text-xs text-slate-500 mt-3">
                                <span className="text-red-400">*</span> Submitting will open a pre-filled chat in your WhatsApp application.
                            </p>
                        </div>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}